import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X as XIcon, Zap, Lock, ArrowRight } from "lucide-react";
import SEOHead, { organizationSchema, faqSchema, breadcrumbSchema } from "@/components/SEOHead";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { ALL_FAQS } from "@/lib/seoData";
import { useAuth } from "@/contexts/AuthContext";

const TIERS = [
  {
    id: "free",
    name: "Free",
    badge: null,
    price: "$0",
    cadence: "forever",
    sub: "Two lists, real AI breakdown, focus timer. Forever free.",
    features: [
      { v: true, t: "2 task lists (Work + Personal defaults)" },
      { v: true, t: "AI task breakdown" },
      { v: true, t: "Focus timer + sound cues" },
      { v: true, t: "Install to home screen (PWA)" },
      { v: true, t: "Public read-only share links" },
      { v: false, t: "Unlimited custom lists" },
      { v: false, t: "Share lists with another user" },
      { v: false, t: "Share entire workspace" },
    ],
    ctaLabel: "Start free",
    ctaTo: "/login",
    ctaTestId: "pricing-cta-free",
  },
  {
    id: "monthly",
    name: "Pro · Monthly",
    badge: "Most popular",
    price: "$15",
    cadence: "AUD / month",
    sub: "Everything in Free, unlocked. Bill month-to-month.",
    pkg: "monthly",
    annualPrice: null,
    features: [
      { v: true, t: "Everything in Free" },
      { v: true, t: "Unlimited custom task lists" },
      { v: true, t: "Share lists with another user" },
      { v: true, t: "3 permission levels: View / Edit / Manage" },
      { v: true, t: "Share entire workspace" },
      { v: true, t: '"Shared with me" section' },
      { v: true, t: "Priority support" },
    ],
    ctaLabel: "Start 14-day trial",
    ctaTestId: "pricing-cta-monthly",
  },
  {
    id: "annual",
    name: "Pro · Annual",
    badge: "Best value · save 33%",
    price: "$120",
    cadence: "AUD / year",
    sub: "Same Pro, paid yearly. Works out to $10/month.",
    pkg: "annual",
    annualPrice: "≈ $10 AUD / month",
    features: [
      { v: true, t: "Everything in Pro Monthly" },
      { v: true, t: "Save $60 / year vs monthly" },
      { v: true, t: "Locked-in pricing for 12 months" },
    ],
    ctaLabel: "Start 14-day trial",
    ctaTestId: "pricing-cta-annual",
  },
];

const FEATURE_MATRIX = [
  ["Task lists", "2 (defaults only)", "Unlimited", "Unlimited"],
  ["AI task breakdown", "Yes", "Yes", "Yes"],
  ["Focus timer + audio", "Yes", "Yes", "Yes"],
  ["Install to home screen", "Yes", "Yes", "Yes"],
  ["Read-only share link per task", "Yes", "Yes", "Yes"],
  ["Share a list with another user", "—", "Yes", "Yes"],
  ["Share whole workspace", "—", "Yes", "Yes"],
  ["Permission levels (View / Edit / Manage)", "—", "Yes", "Yes"],
  ["Billing cadence", "—", "Monthly", "Annual (save $60)"],
];

export default function PricingPage() {
  const { user } = useAuth();
  const isLoggedIn = !!user;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Pricing — BreakItDown ($15 / mo or $120 / yr AUD · 14-day free trial)"
        description="Free forever for 2 lists + AI task breakdown. Pro adds unlimited lists, collaboration, and 3 permission levels — $15 AUD / month or $120 AUD / year. 14-day free trial on Pro."
        path="/pricing"
        schemas={[
          organizationSchema(),
          faqSchema(ALL_FAQS.pricing),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Pricing", path: "/pricing" },
          ]),
        ]}
      />
      <MarketingHeader />

      <main className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-20">
        <header className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">
            Pricing
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-zinc-900">
            Free for solo focus.<br />
            <span className="text-[#002FA7]">Pro</span> when you bring a friend.
          </h1>
          <p className="text-base md:text-lg text-zinc-600 mt-6 max-w-xl leading-relaxed">
            Every plan ships the AI breakdown, focus timer, and install-to-home-screen.
            Pro unlocks unlimited lists and real-time collaboration with another account.
          </p>
        </header>

        {/* Tier cards */}
        <div className="grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mt-12">
          {TIERS.map((tier) => (
            <TierCard key={tier.id} tier={tier} isLoggedIn={isLoggedIn} />
          ))}
        </div>

        {/* Comparison */}
        <section className="mt-20">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900 mb-6">Compare plans</h2>
          <div className="border border-zinc-200 overflow-x-auto -mx-5 sm:mx-0">
            <table className="w-full text-sm min-w-[620px]" data-testid="pricing-comparison">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Feature</th>
                  <th className="text-left p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Free</th>
                  <th className="text-left p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Pro · Monthly</th>
                  <th className="text-left p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Pro · Annual</th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_MATRIX.map(([feature, free, monthly, annual], i) => (
                  <tr key={i} className="border-b border-zinc-200 last:border-0">
                    <td className="p-3 text-zinc-700">{feature}</td>
                    <td className="p-3 text-zinc-600">{free}</td>
                    <td className="p-3 text-zinc-900 font-medium">{monthly}</td>
                    <td className="p-3 text-zinc-900 font-medium">{annual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-20" data-testid="pricing-faq">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900 mb-6">Pricing FAQs</h2>
          <div className="space-y-3 max-w-3xl">
            {PRICING_FAQS.map((f, i) => (
              <details key={i} className="border border-zinc-200 group">
                <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-zinc-900 flex items-center justify-between list-none">
                  {f.q}
                  <span className="text-zinc-400 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-4 pb-3 text-sm text-zinc-600 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}

function TierCard({ tier, isLoggedIn }) {
  const accent = tier.id === "monthly";
  const annual = tier.id === "annual";
  return (
    <div className={`bg-white p-6 md:p-8 relative ${accent ? "md:-my-2 md:py-10 ring-1 ring-zinc-900" : ""}`} data-testid={`tier-${tier.id}`}>
      {tier.badge && (
        <div className={`inline-block text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-1 mb-4 ${accent ? "bg-[#002FA7] text-white" : "border border-zinc-900 text-zinc-900"}`}>
          {tier.badge}
        </div>
      )}
      <h3 className="text-lg font-semibold tracking-tight text-zinc-900">{tier.name}</h3>
      <div className="mt-3 flex items-end gap-2">
        <span className="text-5xl font-semibold tracking-tight text-zinc-900">{tier.price}</span>
        <span className="text-sm text-zinc-500 pb-2">{tier.cadence}</span>
      </div>
      {tier.annualPrice && (
        <p className="text-xs text-zinc-500 mt-1">{tier.annualPrice}</p>
      )}
      <p className="text-sm text-zinc-600 mt-3 min-h-[2.5em]">{tier.sub}</p>

      <CtaButton tier={tier} isLoggedIn={isLoggedIn} accent={accent || annual} />

      <ul className="mt-6 space-y-2.5">
        {tier.features.map((f, i) => (
          <li key={i} className={`flex items-start gap-2 text-sm ${f.v ? "text-zinc-900" : "text-zinc-400"}`}>
            {f.v ? <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" /> : <XIcon className="w-4 h-4 mt-0.5 shrink-0" />}
            {f.t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CtaButton({ tier, isLoggedIn, accent }) {
  // Free tier just routes to login/signup. Pro tiers route to /billing with package param.
  if (tier.id === "free") {
    return (
      <Link
        to="/login"
        className={`block text-center mt-6 px-5 py-3 text-xs font-medium uppercase tracking-[0.12em] transition-colors ${
          accent ? "bg-zinc-900 hover:bg-[#002FA7] text-white" : "border border-zinc-300 hover:border-zinc-900 text-zinc-900"
        }`}
        data-testid={tier.ctaTestId}
      >
        {tier.ctaLabel}
      </Link>
    );
  }
  return (
    <Link
      to={isLoggedIn ? `/billing?pkg=${tier.pkg}` : `/login?next=/billing?pkg=${tier.pkg}`}
      className={`block text-center mt-6 px-5 py-3 text-xs font-medium uppercase tracking-[0.12em] transition-colors ${
        accent ? "bg-[#002FA7] hover:bg-zinc-900 text-white" : "bg-zinc-900 hover:bg-[#002FA7] text-white"
      }`}
      data-testid={tier.ctaTestId}
    >
      {tier.ctaLabel}
    </Link>
  );
}

const PRICING_FAQS = [
  {
    q: "How does the 14-day free trial work?",
    a: "Click 'Start 14-day trial' as a logged-in user. You get full Pro access immediately — no card required. On day 15 your account drops back to Free unless you've upgraded. All your data stays; lists beyond the 2-list cap simply become read-only until you upgrade.",
  },
  {
    q: "How does billing work?",
    a: "Pro Monthly is $15 AUD and Pro Annual is $120 AUD — both charged via Stripe Checkout. You're billed for the period upfront and get Pro access for 30 or 365 days respectively. You can renew or switch cadence any time from your Billing page.",
  },
  {
    q: "Can I cancel any time?",
    a: "Yes. You won't be charged again — your Pro access stays active until the period you've paid for ends, then your account returns to Free. No data is deleted.",
  },
  {
    q: "Do you offer refunds?",
    a: "If something's not working for you, email Charlotte within 14 days of your purchase and we'll refund it in full. No questions about why.",
  },
  {
    q: "What collaboration features come with Pro?",
    a: "Share any task list — or your whole workspace — with another BreakItDown user by email. Pick their permission level: View only, Edit tasks, or Manage list (rename, delete, change permissions). They see shared lists in a 'Shared with me' section.",
  },
  {
    q: "Is the AI task breakdown free or paid?",
    a: "AI task breakdown works on every plan, including Free. The same Claude-powered breakdown is available to everyone — Pro just unlocks more lists and collaboration.",
  },
  {
    q: "What happens to my existing lists if I downgrade?",
    a: "Your data is never deleted. If you have more than 2 lists when you drop to Free, your 2 oldest lists stay editable and the rest become read-only. They unlock again the moment you upgrade.",
  },
];
